import api from '@/libs/axiosInstance';
import { planFeaturesQueryKey } from '@/hooks/queries/use-plan-features-query';
import type {
  ApiResponse,
  BulkCreateCollabsRequest,
  BulkCreateCollabsResponse,
  CollabBulkResult,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trackProductAction } from '@/libs/product-analytics';

/**
 * Create-and-send N collabs in one call (1..50 recipients, identical terms —
 * product arbitrage v1). Each recipient is processed in its own transaction
 * server-side, so one failure never cancels the others; the per-recipient
 * outcome (lookupKey or failure code/message) comes back in `results` and
 * feeds the wizard's done screen. A gifting send moves the month's counter,
 * so the plan counters are read again.
 */
export const useSendCollabsBulkAction = () => {
  const queryClient = useQueryClient();
  return useMutation<CollabBulkResult[], Error, BulkCreateCollabsRequest>({
    mutationFn: (body) =>
      trackProductAction('collabs', 'send', async () => {
        const { data } = await api.post<ApiResponse<BulkCreateCollabsResponse>>(
          '/dashboard/collabs/bulk',
          body
        );
        return data.data?.results ?? [];
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planFeaturesQueryKey });
    },
  });
};
